import {
  Box,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useDisclosure,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';

import {
  useGetLoanProductsAndAssociatedFacilitiesForCreditFacilityQuery,
  AssignmentConfirmationJobGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { Typography } from 'packages/common-frontend/components/Typography';

export const RepurchaseModal = ({
  onConfirm,
  onClose,
  isOpen,
}: {
  onConfirm: () => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const cancelRef = React.useRef(null);

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader
            fontSize="24px"
            fontWeight="bold"
            p="40px 40px 16px"
          >
            Confirm Repurchase
          </AlertDialogHeader>

          <AlertDialogBody p="8px 40px 16px">
            <Typography variant="base-muted">
              Are you sure you want to repurchase the selected receivables and
              move them to the balance sheet?
            </Typography>
          </AlertDialogBody>

          <AlertDialogFooter p="16px 40px 40px">
            <Button
              variant="outline"
              colorScheme="aurora"
              ref={cancelRef.current}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="aurora"
              onClick={() => {
                onConfirm();
                onClose();
              }}
              ml="32px"
            >
              Confirm Repurchase
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export const TransferModal = ({
  onConfirm,
  onClose,
  isOpen,
  loanProductsAndFacilitiesForTransfer,
}: {
  onConfirm: (
    lpCfReassignmentInfo: {
      loanProductId: string;
      creditFacilityId: string | null;
    }[],
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>,
  ) => void;
  isOpen: boolean;
  onClose: () => void;
  loanProductsAndFacilitiesForTransfer: {
    loanProductId: string;
    loanProductName: string;
    creditFacilities: {
      id: string;
      name: string;
    }[];
  }[];
}) => {
  const [
    loanProductCreditFacilityMap,
    setLoanProductCreditFacilityMap,
  ] = useState<{ [loanProductId: string]: string }>({});

  useEffect(() => {
    const map: { [loanProductId: string]: string } = {};
    loanProductsAndFacilitiesForTransfer.forEach((loanProduct) => {
      map[loanProduct.loanProductId] = loanProduct.creditFacilities[0].id;
    });
    setLoanProductCreditFacilityMap(map);
  }, [loanProductsAndFacilitiesForTransfer]);

  const cancelRef = React.useRef(null);

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader
            fontSize="24px"
            fontWeight="bold"
            p="40px 40px 16px"
          >
            Transfer Receivables
          </AlertDialogHeader>

          <AlertDialogBody p="8px 40px 16px">
            <Typography variant="base-muted">
              Select a facility for transfer for each Loan Product.
            </Typography>
            {loanProductsAndFacilitiesForTransfer.map((transferInfo) => {
              const creditFacilityOptions = transferInfo.creditFacilities.map(
                (facility) => ({
                  label: facility.name,
                  value: facility.id,
                }),
              );
              return (
                <Box key={transferInfo.loanProductId} mt="24px">
                  <Typography
                    TextProps={{
                      fontSize: '16px',
                      fontWeight: 600,
                      mb: '16px',
                    }}
                  >
                    {transferInfo.loanProductName}
                  </Typography>
                  <Select
                    options={creditFacilityOptions}
                    value={
                      loanProductCreditFacilityMap[transferInfo.loanProductId]
                    }
                    onChange={(newCreditFacilityId?: string) => {
                      if (newCreditFacilityId) {
                        setLoanProductCreditFacilityMap({
                          ...loanProductCreditFacilityMap,
                          [transferInfo.loanProductId]: newCreditFacilityId,
                        });
                      }
                    }}
                    buttonProps={{ background: 'white' }}
                  />
                </Box>
              );
            })}
          </AlertDialogBody>

          <AlertDialogFooter p="16px 40px 40px">
            <Button
              variant="outline"
              colorScheme="aurora"
              ref={cancelRef.current}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="aurora"
              onClick={() => {
                const lpCfReassignmentInfo = Object.entries(
                  loanProductCreditFacilityMap,
                ).map(([loanProductId, creditFacilityId]) => {
                  return {
                    loanProductId,
                    creditFacilityId,
                  };
                });
                onConfirm(lpCfReassignmentInfo, 'transfer');
                onClose();
              }}
              ml="32px"
            >
              Confirm Transfer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export const BulkActionsButton = ({
  confirmAction,
  queryRowsSelectedCount,
  jobInProgress,
  creditFacilityId,
  loanProductId,
}: {
  confirmAction: (
    lpCfReassignmentInfo: {
      loanProductId: string;
      creditFacilityId: string | null;
    }[],
    jobType: Omit<AssignmentConfirmationJobGQLType['jobType'], 'assignment'>,
  ) => void;
  queryRowsSelectedCount: number;
  jobInProgress: boolean;
  creditFacilityId: string;
  loanProductId: string;
}) => {
  const {
    isOpen: isRepurchaseDialogOpen,
    onOpen: onRepurchaseDialogOpen,
    onClose: onRepurchaseDialogClose,
  } = useDisclosure();

  const {
    isOpen: isTransferDialogOpen,
    onOpen: onTransferDialogOpen,
    onClose: onTransferDialogClose,
  } = useDisclosure();

  const [
    loanProductsAndFacilitiesForTransfer,
    setLoanProductsAndFacilitiesForTransfer,
  ] = useState<
    {
      loanProductId: string;
      loanProductName: string;
      creditFacilities: {
        id: string;
        name: string;
      }[];
    }[]
  >();

  const {
    data: creditFacilityData,
  } = useGetLoanProductsAndAssociatedFacilitiesForCreditFacilityQuery({
    variables: { creditFacilityId },
    onCompleted(data) {
      const transferInfo: {
        loanProductId: string;
        loanProductName: string;
        creditFacilities: {
          id: string;
          name: string;
        }[];
      }[] = [];

      const { loanProducts } = data.creditFacility;

      const filteredLoanProducts =
        loanProductId === 'all'
          ? loanProducts
          : loanProducts.filter((lp) => lp.id === loanProductId);

      filteredLoanProducts.forEach((loanProduct) => {
        const facilitiesForTransfer = loanProduct.currentCreditFacilities
          ? loanProduct.currentCreditFacilities.filter(
              (facility) => facility.id !== creditFacilityId,
            )
          : [];
        if (facilitiesForTransfer.length > 0) {
          transferInfo.push({
            loanProductId: loanProduct.id,
            loanProductName: loanProduct.name,
            creditFacilities: facilitiesForTransfer.map((facility) => ({
              id: facility.id,
              name: facility.name,
            })),
          });
        }
      });

      setLoanProductsAndFacilitiesForTransfer(transferInfo);
    },
    fetchPolicy: 'network-only',
  });

  if (!loanProductsAndFacilitiesForTransfer || !creditFacilityData) {
    return null;
  }

  const canTransfer = loanProductsAndFacilitiesForTransfer.length > 0;

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          leftIcon={
            queryRowsSelectedCount > 0 ? (
              <Box
                background={colors.gray[100]}
                borderRadius="4px"
                padding="4px 8px"
              >
                {queryRowsSelectedCount}
              </Box>
            ) : undefined
          }
          rightIcon={<Icon icon="chevron-down" />}
          variant="secondary-muted"
          isDisabled={queryRowsSelectedCount === 0 || jobInProgress}
          width="fit-content"
          color="gray.800"
        >
          Bulk Action
        </MenuButton>
        <MenuList zIndex={10}>
          <MenuItem
            onClick={() => {
              onRepurchaseDialogOpen();
            }}
          >
            Repurchase to Balance Sheet
          </MenuItem>
          {canTransfer && (
            <MenuItem
              onClick={() => {
                onTransferDialogOpen();
              }}
            >
              Transfer
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      <RepurchaseModal
        isOpen={isRepurchaseDialogOpen}
        onClose={onRepurchaseDialogClose}
        onConfirm={() => {
          const lpCfReassignmentInfo = creditFacilityData.creditFacility.loanProducts.map(
            (lp) => {
              return {
                loanProductId: lp.id,
                creditFacilityId: null,
              };
            },
          );
          confirmAction(lpCfReassignmentInfo, 'repurchase');
        }}
      />
      {canTransfer && (
        <TransferModal
          isOpen={isTransferDialogOpen}
          onClose={onTransferDialogClose}
          onConfirm={confirmAction}
          loanProductsAndFacilitiesForTransfer={
            loanProductsAndFacilitiesForTransfer
          }
        />
      )}
    </>
  );
};
